const formatNumberWithUniqueCommaPattern = (
  num: number,
  decimalPlaces?: number
): string => {
  /**
   * Formats a number by placing a comma between every three digits. Additionally,
   * a comma is placed before the last three digits of the number.
   *
   * Example:
   * - Given the number 1234567.123, the function will return '1,234,567.123'.
   * - If the decimal places is passed it will round off till those places and pad them end with ending 0
   *
   * @param {number} num - The number to be formatted.
   * @returns {string} - The formatted string representation of the number.
   */

  let passedNumber = num;

  if (decimalPlaces !== undefined) {
    passedNumber = Number(passedNumber.toFixed(decimalPlaces));
  }

  const [integerPart, decimalPartRaw] = Number.isFinite(passedNumber)
    ? passedNumber.toString().split('.')
    : ['', ''];

  let mainPart = integerPart.slice(0, -3);
  const lastThreeDigits = integerPart.slice(-3);

  mainPart = mainPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let formattedNumber = mainPart;
  if (integerPart.length > 3) {
    formattedNumber = `${mainPart},${lastThreeDigits}`;
  } else {
    formattedNumber = lastThreeDigits;
  }

  if (decimalPartRaw !== undefined) {
    let decimalPart = decimalPartRaw;
    if (decimalPlaces !== undefined) {
      decimalPart = decimalPart.padEnd(decimalPlaces, '0');
    }
    return `${formattedNumber}.${decimalPart}`;
  }
  if (decimalPlaces !== undefined && decimalPlaces > 0) {
    return `${formattedNumber}.${'0'.repeat(decimalPlaces)}`;
  }
  return formattedNumber;
};

/**
 * Formats a decimal fraction as a percentage string.
 *
 * Example:
 * - `formatPercent(0.0625)` returns `'6.25%'`.
 */
const formatFractionAsPercent = (
  rate: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 3,
  }).format(rate);

export { formatFractionAsPercent, formatNumberWithUniqueCommaPattern };
